import { useState, useEffect, useCallback, useRef } from 'react';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

async function fetchVersion() {
  const res = await fetch(`/version.json?t=${Date.now()}`);
  if (!res.ok) return null;
  const data = await res.json();
  return data.version;
}

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baselineRef = useRef(null);
  const dismissedRef = useRef(false);

  const checkVersion = useCallback(async () => {
    const version = await fetchVersion();
    if (!version) return;

    if (!baselineRef.current) {
      baselineRef.current = version;
      return;
    }

    if (version !== baselineRef.current) {
      dismissedRef.current = false;
      setUpdateAvailable(true);
    }
  }, []);

  const dismiss = useCallback(() => {
    dismissedRef.current = true;
    setUpdateAvailable(false);
  }, []);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    checkVersion();
    const interval = setInterval(checkVersion, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [checkVersion]);

  // Re-check on tab focus
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [checkVersion]);

  return { updateAvailable, dismiss, reload };
}
